import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { formatApiError } from "@/lib/api";
import { Lock, Loader2 } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import Logo from "@/assets/Logo.png";

export default function Login() {
  const { t } = useTranslation();
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user && user !== false) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-screen flex bg-white">
      <div className="w-full lg:w-[440px] flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="flex items-center justify-between mb-12">
            <Link to="/" className="flex items-center gap-3">
              <span className="w-12 h-12 text-beige-light grid place-items-center font-bold rounded-sm"><img src={Logo} alt="Refah Europa e.V." className="w-full h-full object-contain" /></span>
              <span className="font-semibold tracking-tight text-slate-900">Refah Europa e.V.</span>
            </Link>
            <LanguageSwitcher />
          </div>

          <div className="text-xs uppercase tracking-[0.3em] text-bordeaux-accent mb-3">{t("admin.login.brand")}</div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 mb-2">{t("admin.login.title")}</h1>
          <p className="text-sm text-slate-600 mb-10">{t("admin.login.subtitle")}</p>

          {params.get("expired") && (
            <div data-testid="session-expired-notice" className="mb-6 border border-amber-200 bg-amber-50 text-amber-800 text-sm px-3 py-2 rounded-sm">
              {t("admin.login.sessionExpired")}
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-widest text-slate-500">{t("admin.login.emailLabel")}</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@example.com"
                data-testid="login-email-input"
                className="mt-2 rounded-sm border-slate-300 focus:border-bordeaux focus:ring-bordeaux"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-widest text-slate-500">{t("admin.login.passwordLabel")}</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="login-password-input"
                className="mt-2 rounded-sm border-slate-300 focus:border-bordeaux focus:ring-bordeaux"
              />
            </div>
            {error && (
              <div data-testid="login-error" className="text-sm text-red-600 border border-red-200 bg-red-50 px-3 py-2 rounded-sm">
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={busy}
              data-testid="login-submit-btn"
              className="w-full bg-bordeaux hover:bg-bordeaux-dark text-beige-light rounded-sm py-6 font-medium"
            >
              {busy ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" /> {t("admin.login.submitting")}
                </>
              ) : (
                <>
                  <Lock size={14} className="mr-2" /> {t("admin.login.submit")}
                </>
              )}
            </Button>
          </form>

          <Link to="/" className="block text-center text-xs uppercase tracking-widest text-slate-500 hover:text-bordeaux mt-12">
            {t("admin.login.backToSite")}
          </Link>
        </div>
      </div>

      <div className="hidden lg:block flex-1 relative grain overflow-hidden">
        <div className="absolute inset-0 bg-bordeaux" />
        <img
          src="https://images.pexels.com/photos/6650988/pexels-photo-6650988.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200"
          alt=""
          className="absolute inset-0 w-full h-full object-cover mix-blend-luminosity opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-bordeaux-deeper/80 via-bordeaux/40 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-beige-light max-w-md">
          <div className="text-xs uppercase tracking-[0.3em] text-beige mb-4">{t("admin.login.visualEyebrow")}</div>
          <h2 className="text-3xl font-semibold tracking-tight">{t("admin.login.visualTitle")}</h2>
        </div>
      </div>
    </div>
  );
}
